import { apiClient } from "@/lib/api/client";
import { getDataOrThrowApiError } from "@/lib/api/error";
import type {
  UpdateSoftwareArchitectureReq,
  UpdateSoftwareDetailReq,
} from "@/routes/_staff/software/$id/_$id/-query/type";

import type {
  CustomAppState,
  GetAppIntegrationUsersParams,
  MdmActivateReq,
  MdmActivateRes,
  MdmConfigurationRes,
  PatchExternalTenantSettingsReq,
  ReAuthenticateReq,
  ReAuthenticateRes,
} from "./type";
import type { CustomAppContractState } from "./type";
import type { PricingPlanState } from "./type";

export const getAppIntegrationContract = async (appIntegrationId: string) => {
  const res = await apiClient.GET(
    "/v2/app-integrations/{id}/overview/app-contract",
    { params: { path: { id: appIntegrationId } } },
  );
  return getDataOrThrowApiError(res);
};

export const getAppOverviewUser = async (id: string) => {
  const res = await apiClient.GET(
    "/v2/app-integrations/{id}/overview/user-and-people",
    { params: { path: { id } } },
  );
  return getDataOrThrowApiError(res);
};

export const getAppOverviewBillingCost = async (id: string) => {
  const res = await apiClient.GET(
    "/v2/app-integrations/{id}/overview/billing-cost",
    { params: { path: { id } } },
  );
  return getDataOrThrowApiError(res);
};

export const getAppOverviewUserGrowth = async (id: string) => {
  const res = await apiClient.GET(
    "/v2/app-integrations/{id}/overview/user-growth",
    { params: { path: { id } } },
  );
  return getDataOrThrowApiError(res);
};

export const getAppOverviewMonthlyActiveUserRate = async (id: string) => {
  const res = await apiClient.GET(
    "/v2/app-integrations/{id}/overview/monthly-active-user-rate",
    { params: { path: { id } } },
  );
  return getDataOrThrowApiError(res);
};

export const getAppOverviewMonthlyCost = async (id: string) => {
  const res = await apiClient.GET(
    "/v2/app-integrations/{id}/overview/monthly-cost",
    { params: { path: { id } } },
  );
  return getDataOrThrowApiError(res);
};

export const getAppOverviewRenewalDate = async (id: string) => {
  const res = await apiClient.GET(
    "/v2/app-integrations/{id}/overview/renewal-date",
    { params: { path: { id } } },
  );
  return getDataOrThrowApiError(res);
};

export const getAppOverviewSavingCost = async (id: string) => {
  const res = await apiClient.GET(
    "/v2/app-integrations/{id}/overview/saving-cost",
    { params: { path: { id } } },
  );
  return getDataOrThrowApiError(res);
};

export const getAppOverviewRecommendedActions = async (id: string) => {
  const res = await apiClient.GET(
    "/v2/app-integrations/{id}/overview/recommended-actions",
    { params: { path: { id } } },
  );
  return getDataOrThrowApiError(res);
};

export const getCountByCategories = async (appIntegrationId: string) => {
  const res = await apiClient.GET("/v3/app-integrations/{id}/users/count", {
    params: { path: { id: appIntegrationId } },
  });
  return getDataOrThrowApiError(res);
};

// Refresh resources
export const postRefreshResources = async (id: string) => {
  const res = await apiClient.POST(
    "/v2/app-integrations/{id}/refresh-resources",
    {
      params: { path: { id } },
    },
  );
  return getDataOrThrowApiError(res);
};

export const getRefreshResourcesStatus = async (id: string) => {
  const res = await apiClient.GET(
    "/v2/app-integrations/{id}/refresh-resources/status",
    {
      params: { path: { id } },
    },
  );
  return getDataOrThrowApiError(res);
};

export const getIntegratableApp = async (appId: string) => {
  const res = await apiClient.GET("/v2/apps/{id}", {
    params: { path: { id: appId } },
  });
  return getDataOrThrowApiError(res);
};

export const getAppIntegrationSettings = async (appIntegrationId: string) => {
  const res = await apiClient.GET("/v2/app-integrations/{id}/settings", {
    params: { path: { id: appIntegrationId } },
  });
  return getDataOrThrowApiError(res);
};

export const deactivateAppIntegration = async (appIntegrationId: string) => {
  const res = await apiClient.PATCH(
    "/v2/app-integrations/{id}/settings/deactivate",
    {
      params: { path: { id: appIntegrationId } },
    },
  );
  return getDataOrThrowApiError(res);
};

export const deleteAppIntegration = async (appIntegrationId: string) => {
  const res = await apiClient.DELETE("/v2/app-integrations/{id}", {
    params: { path: { id: appIntegrationId } },
  });
  return getDataOrThrowApiError(res);
};

export const reactivateAppIntegration = async (appIntegrationId: string) => {
  const res = await apiClient.PATCH(
    "/v2/app-integrations/{id}/settings/reactivate",
    {
      params: { path: { id: appIntegrationId } },
    },
  );
  return getDataOrThrowApiError(res);
};

export const patchExternalTenantSettings = async (
  appIntegrationId: string,
  tenantData: PatchExternalTenantSettingsReq,
) => {
  const res = await apiClient.PATCH(
    "/v2/app-integrations/{id}/settings/external-tenant",
    {
      params: {
        path: {
          id: appIntegrationId,
        },
      },
      body: tenantData,
    },
  );
  return getDataOrThrowApiError(res);
};

export const updateCustomApp = async (
  appIntegrationId: string,
  appState: CustomAppState,
) => {
  const res = await apiClient.PATCH(
    "/v2/app-integrations/{id}/settings/custom-app",
    {
      params: { path: { id: appIntegrationId } },
      body: { ...appState },
    },
  );
  return getDataOrThrowApiError(res);
};

export const getPresignedUrl = async (filename: string) => {
  const res = await apiClient.GET("/v2/s3/presigned-url/image", {
    params: { query: { filename } },
  });
  return getDataOrThrowApiError(res);
};

export const updateCustomAppContract = async (
  appIntegrationId: string,
  body: CustomAppContractState,
) => {
  const res = await apiClient.PATCH(
    "/v2/app-integrations/{id}/settings/custom-app-contract",
    {
      params: { path: { id: appIntegrationId } },
      body,
    },
  );
  return getDataOrThrowApiError(res);
};

export const updatePricingPlan = async (
  appIntegrationId: string,
  pricingData: PricingPlanState,
) => {
  const res = await apiClient.PATCH(
    "/v2/app-integrations/{id}/settings/pricing-plan",
    {
      params: { path: { id: appIntegrationId } },
      body: pricingData,
    },
  );
  return getDataOrThrowApiError(res);
};

export const updateAppUserProvisioning = async (
  appIntegrationId: string,
  body: { USER_PROVISIONING: boolean },
) => {
  const res = await apiClient.PATCH(
    "/v2/app-integrations/{id}/settings/features",
    {
      params: { path: { id: appIntegrationId } },
      body,
    },
  );
  return getDataOrThrowApiError(res);
};

export const getAppUsers = async (
  appIntegrationId: string,
  search: GetAppIntegrationUsersParams,
) => {
  const res = await apiClient.GET("/v3/app-integrations/{id}/users", {
    params: {
      path: { id: appIntegrationId },
      query: search,
    },
  });
  return getDataOrThrowApiError(res);
};

export const reAuthenticate = async (
  appIntegrationId: string,
  data: ReAuthenticateReq,
): Promise<ReAuthenticateRes> => {
  const res = await apiClient.POST(
    "/v2/app-integrations/{id}/settings/authenticate",
    {
      params: { path: { id: appIntegrationId } },
      body: data,
    },
  );
  return getDataOrThrowApiError(res);
};

export const deleteCustomAppUser = async (
  appIntegrationId: string,
  appUserId: string,
) => {
  const res = await apiClient.DELETE(
    "/v3/app-integrations/{id}/users/{appUserId}",
    {
      params: { path: { id: appIntegrationId, appUserId } },
    },
  );
  return getDataOrThrowApiError(res);
};

export const activateMdmAuthentication = async (
  appIntegrationId: string,
  data: MdmActivateReq,
): Promise<MdmActivateRes> => {
  const res = await apiClient.POST(
    "/v2/app-integrations/{id}/settings/mdm-activate",
    {
      params: { path: { id: appIntegrationId } },
      body: data,
    },
  );
  return getDataOrThrowApiError(res);
};

// TODO: MDMエンドポイントがバックエンドで実装されたら有効化
export const getMdmConfiguration = (
  _appIntegrationId: string,
): Promise<MdmConfigurationRes> => {
  // const res = await apiClient.GET(
  //   "/v2/app-integrations/{id}/settings/mdm-configuration",
  //   {
  //     params: { path: { id: appIntegrationId } },
  //   },
  // );
  // return getDataOrThrowApiError(res);

  // 一時的なモック実装
  return Promise.resolve({
    tenantId: undefined,
    clientId: undefined,
    hasClientSecret: false,
    lastUpdated: undefined,
  });
};

// Software data from app-integration
export const getSoftwareFromAppIntegration = async (
  appIntegrationId: string,
) => {
  const res = await apiClient.GET(
    "/v2/software/app-integrations/{appIntegrationId}",
    {
      params: { path: { appIntegrationId } },
    },
  );
  return getDataOrThrowApiError(res);
};

export const createSoftwareFromAppIntegration = async (
  appIntegrationId: string,
) => {
  const res = await apiClient.POST(
    "/v2/software/app-integrations/{appIntegrationId}",
    {
      params: { path: { appIntegrationId } },
    },
  );
  return getDataOrThrowApiError(res);
};

// Software update mutations (using existing software API)
export const updateSoftwareDetail = async (
  softwareId: string,
  data: UpdateSoftwareDetailReq,
) => {
  const res = await apiClient.PUT("/v2/software/{softwareId}/detail", {
    params: { path: { softwareId } },
    body: data,
  });
  return getDataOrThrowApiError(res);
};

export const updateSoftwareArchitecture = async (
  softwareId: string,
  data: UpdateSoftwareArchitectureReq,
) => {
  const res = await apiClient.PUT("/v2/software/{softwareId}/architecture", {
    params: { path: { softwareId } },
    body: data,
  });
  return getDataOrThrowApiError(res);
};
